/* eslint-disable import/order */
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Toolbar from '@mui/material/Toolbar';
import { FC } from 'react';

import { defineStyles, theme } from '../../theme';

import { ServiceBadge } from '../ServiceBadge';
import TopBarProjectLink from './TopBarProjectLink';
import { UserMenu } from '../UserMenu';
import { useAuthentication } from '../../providers/AuthProvider/AuthContext';
import useProjectSurveyQuery from '../../api/hooks/queries/useProjectSurveyQuery';
import useQreIdData from '../../hooks/useQreIdData';
import IconButton from '@mui/material/IconButton';
import { buildLinuxBoxUrl } from '../../utils/linuxBox';
import { useConfig } from '../../providers/ConfigProvider/ConfigContext';

const styles = defineStyles({
  root: {
    display: 'flex',
    justifyContent: 'center',
    '& svg': {
      mr: 1,
    },
  },
  toolbar: {
    '&.MuiToolbar-root': {
      minHeight: 40,
    },
  },
  centerLinks: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1.25,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    '& *': {
      color: theme.vars.palette.common.white,
    },
  },
});

export type TopBarProps = {
  qreId: string;
};

export const TopBar: FC<TopBarProps> = ({ qreId }) => {
  const { user } = useAuthentication();
  const { data, isLoading } = useProjectSurveyQuery();
  const { projectId, surveyId, url } = useQreIdData();
  const { linuxBoxUrl } = useConfig();

  const insightStreamUrl = `/insight/stream?aggregate=cz.commity.cati-evs.domain.cati.aggregate.survey%2Fsurvey&qre-id=${encodeURIComponent(
    qreId
  )}&show-events=t`;

  return (
    <Box component="header" sx={styles.root}>
      <AppBar position="relative">
        <Toolbar variant="dense" sx={styles.toolbar}>
          <TopBarProjectLink
            qreId={qreId}
            projectName={data?.name || ''}
            surveyNumber={data?.surveyNumber}
            isLoading={isLoading}
          />
          <Box sx={styles.centerLinks}>
            <ServiceBadge kind="qb" href={`https://${url}/builder/${projectId}/operations?surveyId=${surveyId}`} />
            <ServiceBadge
              kind="catiex"
              isError={!data?.catiExProjectLink}
              href={data?.catiExProjectLink ?? undefined}
            />
            <ServiceBadge kind="lb" isError={!data?.linuxBoxCreated} href={buildLinuxBoxUrl(linuxBoxUrl, qreId)} />
          </Box>
          <Box id="topbar-actions-container" sx={styles.actions} />
          {user && <UserMenu user={user} />}
          <IconButton aria-haspopup="true" onClick={() => window.open(insightStreamUrl, '_blank')}>
            <VisibilityIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
    </Box>
  );
};
